import React, { useState, useCallback } from 'react';

// Must match CONSENT_STATEMENT_ID / CONSENT_STATEMENT_TEXT in seeding_content.js.
const CONSENT_STATEMENT_ID = 'amr-m0-consent-v1';
const CONSENT_STATEMENT_TEXT =
    'I confirm the client has consented to AMR generating draft seeding content for human review. ' +
    'Nothing will be published automatically.';

// Must match GENERATION_UNAVAILABLE_MESSAGE in seeding_content.js.
const GENERATION_UNAVAILABLE_MESSAGE =
    'Draft generation is temporarily unavailable until verified, approved brand facts are configured.';

// M0: no verified, approved fact source exists yet (roadmap step M1), so the
// server refuses every generation request (fail closed). The UI states this
// explicitly and does not offer the Generate action. The server enforces the
// refusal independently; M1 must change both, never this flag alone.
const DRAFT_GENERATION_AVAILABLE = false;

/**
 * BoostModal — draft seeding artifact generation (M0 truth containment).
 *
 * - Explicit operator consent (checkbox) is required for every request
 * - Content is generated from verified, approved facts only; the server
 *   refuses (fails closed) when none are available
 * - Generated artifacts are drafts for human review: never shown as
 *   published, active or as having any estimated effect
 * - No Emberlight / AMR score or target is shown here: its data source and
 *   semantics are pending separate validation
 */
export default function BoostModal({ clientId, clientName, onClose, onBoostComplete, authFetch }) {
    const [step, setStep] = useState('consent'); // consent | configure | result
    const [consentChecked, setConsentChecked] = useState(false);
    const [platform, setPlatform] = useState('schema');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState('');

    const handleBoost = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const fetchFn = authFetch || fetch;
            const res = await fetchFn('/api/boost', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clientId,
                    consent: consentChecked === true,
                    consentStatementId: CONSENT_STATEMENT_ID,
                    platform,
                }),
            });

            const data = await res.json();

            if (!data.ok) {
                setError(data.reason_code === 'NO_VERIFIED_FACT_SOURCE'
                    ? GENERATION_UNAVAILABLE_MESSAGE
                    : (data.error || 'Request refused.'));
                setLoading(false);
                return;
            }

            setResult(data);
            setStep('result');
        } catch (err) {
            setError('Failed to process the request. Please try again.');
        }
        setLoading(false);
    }, [clientId, platform, consentChecked, authFetch]);

    const handleCopy = useCallback((text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(label);
            setTimeout(() => setCopied(''), 2000);
        });
    }, []);

    // Format content output based on type
    const renderGeneratedContent = () => {
        if (!result?.generatedContent) return null;
        const { contentType, generatedContent } = result;

        if (contentType === 'schema-jsonld') {
            const jsonStr = JSON.stringify(generatedContent, null, 2);
            return (
                <div className="boost-content-output">
                    <div className="boost-content-header">
                        <span>📋 Schema.org JSON-LD — paste into your website's {'<head>'}</span>
                        <button
                            className="btn-copy"
                            onClick={() => handleCopy(`<script type="application/ld+json">\n${jsonStr}\n</script>`, 'schema')}
                        >
                            {copied === 'schema' ? '✅ Copied!' : '📋 Copy'}
                        </button>
                    </div>
                    <pre className="boost-code-block">{`<script type="application/ld+json">\n${jsonStr}\n</script>`}</pre>
                </div>
            );
        }

        if (contentType === 'faq-content') {
            return (
                <div className="boost-content-output">
                    <div className="boost-content-header">
                        <span>📋 FAQ Content — 3 formats available</span>
                    </div>

                    <div className="boost-content-tabs">
                        <div className="boost-tab-section">
                            <div className="boost-tab-header">
                                <span>JSON-LD (for {'<head>'})</span>
                                <button
                                    className="btn-copy"
                                    onClick={() => handleCopy(`<script type="application/ld+json">\n${JSON.stringify(generatedContent.jsonLd, null, 2)}\n</script>`, 'faq-jsonld')}
                                >
                                    {copied === 'faq-jsonld' ? '✅' : '📋'}
                                </button>
                            </div>
                            <pre className="boost-code-block boost-code-sm">{JSON.stringify(generatedContent.jsonLd, null, 2).substring(0, 300)}...</pre>
                        </div>

                        <div className="boost-tab-section">
                            <div className="boost-tab-header">
                                <span>HTML Snippet</span>
                                <button
                                    className="btn-copy"
                                    onClick={() => handleCopy(generatedContent.htmlSnippet, 'faq-html')}
                                >
                                    {copied === 'faq-html' ? '✅' : '📋'}
                                </button>
                            </div>
                            <pre className="boost-code-block boost-code-sm">{generatedContent.htmlSnippet.substring(0, 300)}...</pre>
                        </div>

                        <div className="boost-tab-section">
                            <div className="boost-tab-header">
                                <span>Markdown</span>
                                <button
                                    className="btn-copy"
                                    onClick={() => handleCopy(generatedContent.markdownSnippet, 'faq-md')}
                                >
                                    {copied === 'faq-md' ? '✅' : '📋'}
                                </button>
                            </div>
                            <pre className="boost-code-block boost-code-sm">{generatedContent.markdownSnippet.substring(0, 300)}...</pre>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content boost-modal-wide" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h2>🚀 Boost — {clientName || clientId}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {!DRAFT_GENERATION_AVAILABLE && (
                    <div className="form-message form-error generation-unavailable" role="status">
                        <span>{GENERATION_UNAVAILABLE_MESSAGE}</span>
                    </div>
                )}

                {/* Step 1: Consent */}
                {step === 'consent' && (
                    <div className="modal-body">
                        <div className="consent-notice">
                            <div className="consent-icon">🛡️</div>
                            <h3>Ethics Confirmation</h3>
                            <p>
                                This generates a draft artifact for human review. Nothing is published
                                automatically, and no effect on AI visibility is claimed or estimated.
                            </p>
                            <ul className="consent-checklist">
                                <li>✓ Content states verified, approved facts only — otherwise the request is refused</li>
                                <li>✓ Generated drafts are not published; publication is a separate, authorized step</li>
                                <li>✓ Every request (generated or refused) is written to an append-only audit log</li>
                            </ul>
                            <label className="consent-confirm">
                                <input
                                    type="checkbox"
                                    checked={consentChecked}
                                    onChange={e => setConsentChecked(e.target.checked)}
                                />{' '}
                                {CONSENT_STATEMENT_TEXT}
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="btn btn-boost"
                                onClick={() => consentChecked ? setStep('configure') : null}
                                disabled={!consentChecked}
                            >
                                {consentChecked ? 'Proceed →' : 'Confirm consent to proceed'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Configure Platform */}
                {step === 'configure' && (
                    <div className="modal-body">
                        <h3>Select Draft Type</h3>
                        <div className="platform-select-grid">
                            {[
                                { id: 'schema', label: 'Schema.org JSON-LD', icon: '🔗', desc: 'Organization structured data from verified facts', enabled: true },
                                { id: 'faq', label: 'FAQ Content', icon: '❓', desc: 'FAQ answers stating verified facts only', enabled: true },
                                { id: 'hn', label: 'Hacker News', icon: '🟧', desc: 'Disabled — pending approved channel policy', enabled: false },
                                { id: 'reddit', label: 'Reddit', icon: '🔴', desc: 'Disabled — pending approved channel policy', enabled: false },
                            ].map(p => (
                                <button
                                    key={p.id}
                                    className={`platform-select-card ${platform === p.id ? 'selected' : ''}`}
                                    onClick={() => p.enabled && setPlatform(p.id)}
                                    disabled={!p.enabled}
                                >
                                    <span className="platform-select-icon">{p.icon}</span>
                                    <span className="platform-select-label">{p.label}</span>
                                    <span className="platform-select-desc">{p.desc}</span>
                                </button>
                            ))}
                        </div>

                        {error && <div className="form-message form-error"><span>{error}</span></div>}

                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setStep('consent')}>← Back</button>
                            <button
                                className="btn btn-boost"
                                onClick={handleBoost}
                                disabled={!DRAFT_GENERATION_AVAILABLE || loading || !consentChecked}
                            >
                                {!DRAFT_GENERATION_AVAILABLE
                                    ? 'Draft generation unavailable'
                                    : loading ? 'Generating...' : 'Generate draft artifact'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Generated draft (never shown as published) */}
                {step === 'result' && result && (
                    <div className="modal-body">
                        <div className="boost-result">
                            <div className="boost-result-icon">📝</div>
                            <h3>Draft artifact generated — NOT published</h3>
                            <p className="boost-draft-note">{result.notice}</p>

                            {/* Generated Content Display */}
                            {renderGeneratedContent()}

                            <div className="boost-details">
                                <div className="boost-detail-row">
                                    <span>Draft type:</span><span>{result.contentType}</span>
                                </div>
                                <div className="boost-detail-row">
                                    <span>Publication:</span>
                                    <span className="badge badge-info">Not published</span>
                                </div>
                                <div className="boost-detail-row">
                                    <span>Human review:</span><span>Required</span>
                                </div>
                                <div className="boost-detail-row">
                                    <span>Artifact SHA-256:</span><span>{(result.artifact_sha256 || '').slice(0, 16)}…</span>
                                </div>
                                <div className="boost-detail-row">
                                    <span>Audit:</span><span className="badge badge-success">Logged</span>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn btn-primary" onClick={() => { onBoostComplete && onBoostComplete(result); onClose(); }}>
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
